// brain.ts — the AI client. Oriora Scout is a DEEP-RESEARCH AGENT: each research run is a
// MULTI-STEP loop driven here on the client, with each step a SINGLE billed POST to the Oriora
// brain (the server holds each step's prompt + bills the user's Oriora wallet). This is the
// AGENTIC pattern → Scout is BYOK-ONLY (default_byok=true): Managed AI is forbidden for agentic
// apps (compliance rule, server-enforced), so a user without a vendor key gets a 403 and is told to
// connect one in Oriora Settings.
//
// The loop (one Bearer POST per action — never a single mega-call, never a direct OpenAI/Anthropic
// SDK in the app):
//   1) plan       POST …/plan        { question }                  → { sub_questions[] }
//   2) gather     POST …/gather      { question, sub_question }    → { sources[], notes }   (×N, web-grounded server-side)
//   3) synthesize POST …/synthesize  { question, findings[] }      → { summary, key_points[], sources[] }
// Steps 1 & 3 are one call each; step 2 is one call PER sub-question. Each call carries a status the
// UI surfaces live (the run takes several seconds — we show the steps as they happen).
//
// Whole research history stays on-device (db.rs); only the question + the steps' intermediate text
// leave the device, per call, billed BYOK, then forgotten (stateless server).

import { invoke } from "./tauri-web";
import { getAccessToken } from "./supabase";
import { searchWeb, readResults } from "./search";

const SERVER = "https://api.orioralabs.com";
const SLUG = "oriora_scout";

export type BrainErrorCode =
  | "insufficient_funds"
  | "byok_required" // 403 — agentic app: Managed AI blocked, needs a vendor key (BYOK)
  | "unauthenticated"
  | "unavailable"
  | "no_store"
  | "too_large";

export class BrainError extends Error {
  code: BrainErrorCode;
  constructor(code: BrainErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = "BrainError";
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * The one transport for every billed AI call. Attaches the Oriora session JWT, maps the
 * wallet/auth/BYOK status codes to BrainError, and retries only transient failures.
 *   401 → unauthenticated · 402 → insufficient_funds
 *   403 → byok_required (Scout is agentic ⇒ BYOK-only — connect a vendor key in Oriora Settings)
 *   502/503/504 + network → retried (up to 3×), then "unavailable".
 */
// SSE reader for the streaming fleet chat (request body {stream:true} → text/event-stream):
// `: open` / `: keepalive` comments and [DONE] are ignored, each `data: {"chunk":…}` event
// feeds onChunk, and the `data: {"done":true, …}` event carries the exact old JSON payload —
// returned where res.json() used to be, so all existing action/reply mapping stays untouched.
async function readSseChat(bodyStream: ReadableStream<Uint8Array>, onChunk: (t: string) => void): Promise<Record<string, unknown>> {
  const reader = bodyStream.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done: Record<string, unknown> | null = null;
  for (;;) {
    const { value, done: eof } = await reader.read();
    if (eof) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) !== -1) {
      const line = buf.slice(0, nl).replace(/\r$/, "");
      buf = buf.slice(nl + 1);
      if (!line.startsWith("data:")) continue; // comment (": open"/": keepalive") or blank line
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") continue;
      let ev: Record<string, unknown>;
      try { ev = JSON.parse(payload) as Record<string, unknown>; } catch { continue; }
      if (typeof ev.chunk === "string") onChunk(ev.chunk);
      else if (ev.done) done = ev;
    }
  }
  if (!done) throw new BrainError("unavailable", "The reply stream ended early — please try again.");
  // A streamed reply can end with a billing/auth error instead of a payload ({done:true, error, _status}).
  // Returning it as data dissolved the "Top up" message into an empty action (found live 2026-08-27) —
  // throw the same BrainError the buffered path would.
  if (typeof done.error === "string" && done.error) {
    const st = typeof done._status === "number" ? done._status : 0;
    const msg = typeof done.message === "string" && done.message ? done.message : done.error;
    if (st === 401) throw new BrainError("unauthenticated", msg);
    if (st === 402) throw new BrainError("insufficient_funds", msg);
    if (st === 403) throw new BrainError("byok_required", msg);
    throw new BrainError("unavailable", msg);
  }
  return done;
}

export async function brainCall<T>(endpoint: string, body: unknown, signal?: AbortSignal, onChunk?: (t: string) => void): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  const MAX = 3;
  for (let attempt = 1; attempt <= MAX; attempt++) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    let res: Response;
    try {
      res = await fetch(`${SERVER}/api/apps/${SLUG}/${endpoint}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(onChunk && body && typeof body === "object" ? { ...(body as Record<string, unknown>), stream: true } : body),
        signal,
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") throw err;
      if (attempt < MAX) {
        await sleep(attempt * 800);
        continue;
      }
      throw new BrainError("unavailable", "AI is temporarily unavailable.");
    }
    if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
    if (res.status === 402)
      throw new BrainError("insufficient_funds", "Add funds in your Oriora account to keep researching.");
    if (res.status === 403)
      throw new BrainError(
        "byok_required",
        "Scout needs your own AI provider key. Connect one in Oriora Settings, then try again.",
      );
    if ([502, 503, 504].includes(res.status)) {
      if (attempt < MAX) {
        await sleep(attempt * 800);
        continue;
      }
      throw new BrainError("unavailable", "AI is busy — please try again.");
    }
    if (!res.ok) throw new BrainError("unavailable", "AI request failed.");
    // Streaming fleet chat: with onChunk the body carries stream:true and the server answers
    // as SSE — read it incrementally (readSseChat above); the done event IS the old JSON
    // payload. Anything that is not an event stream (old server, an error status body, a
    // proxy that buffered it away) falls through to the buffered path — never a dead end.
    if (onChunk && res.body && (res.headers.get("content-type") ?? "").includes("text/event-stream")) {
      return (await readSseChat(res.body, onChunk)) as unknown as T;
    }
    return (await res.json()) as T;
  }
  throw new BrainError("unavailable", "AI is temporarily unavailable.");
}

// ── Step contracts (the server route — next stage — must mirror these) ───────────

interface PlanResult {
  /** 2–4 focused sub-questions the agent will research, in order. */
  sub_questions: string[];
}

/** A source returned by a gather step — title + url (+ a short snippet of what it contributed). */
export interface RawSource {
  title: string;
  url: string;
  snippet?: string;
  /** A PHOTOGRAPHED source (on-device JPEG data-URL). "" / absent for a web source. */
  photo_ref?: string;
}

interface GatherResult {
  /** The web sources the server found + grounded this sub-question's notes in. */
  sources: RawSource[];
  /** A short factual digest of what the sources say about the sub-question (used by synthesize). */
  notes: string;
}

interface SynthesizeResult {
  summary: string;
  key_points: { text: string; source_ordinals: number[] }[];
  /** The de-duplicated, ordinal-numbered final source list the key points cite into. */
  sources: { ordinal: number; title: string; url: string; snippet?: string }[];
}

/** What `runResearch` resolves with — the finished, cited brief (already de-duped + numbered).
 *  A source with `photo_ref` set is a PHOTOGRAPHED source the user attached (on-device JPEG
 *  data-URL); its `url` is empty. */
export interface ResearchBrief {
  summary: string;
  key_points: { text: string; source_ordinals: number[] }[];
  sources: { ordinal: number; title: string; url: string; snippet?: string; photo_ref?: string }[];
}

// ── The agentic loop: the multi-second, multi-step run ───────────────────────────

/** Live progress phases — the UI shows these as the loop runs (plan → gather ×N → synthesize). */
export type ResearchPhase = "planning" | "gathering" | "synthesizing" | "done";

export interface ResearchProgress {
  phase: ResearchPhase;
  /** 0..1 — coarse completion for the bar (plan=start, each gather advances, synthesize=near end). */
  fraction: number;
  /** Human line for the live step row, e.g. "Researching: caffeine half-life by age". */
  label: string;
  /** When known: which gather step (1-based) of how many. */
  step?: number;
  total?: number;
  /** Sources discovered so far (so the UI can tick a running count up as gathering proceeds). */
  sourcesFound?: number;
  /** The sub-questions, once planned — shown as a checklist that fills in. */
  subQuestions?: string[];
}

/**
 * Run the full deep-research loop for `question`, reporting progress via `onProgress`. Returns the
 * finished cited brief. Throws BrainError on a hard failure (the caller marks the run `failed`).
 *
 * This is the agentic orchestration: it issues a SEQUENCE of billed calls and narrates each. The
 * `signal` lets the user cancel mid-run (AbortError propagates — the caller treats it as a cancel,
 * not a failure).
 */
export async function runResearch(
  question: string,
  onProgress: (p: ResearchProgress) => void,
  signal?: AbortSignal,
  photoSources: string[] = [],
): Promise<ResearchBrief> {
  // PHOTOGRAPHED SOURCES the user attached (page/report/whiteboard/slide) — downscaled JPEG
  // data-URLs, kept on-device until this run. They are handed to the SYNTHESIZE step as image
  // input so the model actually READS them when writing the brief; that single call is the only
  // time they leave the device. Guard the shape (only real image data-URLs travel).
  const photos = (Array.isArray(photoSources) ? photoSources : [])
    .filter((u) => typeof u === "string" && u.startsWith("data:image/"));
  // 1) PLAN — break the question into focused sub-questions.
  onProgress({ phase: "planning", fraction: 0.05, label: "Planning the research…" });
  const plan = await brainCall<PlanResult>("plan", { question }, signal);
  const subs = (Array.isArray(plan.sub_questions) ? plan.sub_questions : [])
    .map((s) => String(s).trim())
    .filter(Boolean)
    .slice(0, 5);
  // Defensive: a degenerate plan still researches the original question directly.
  const subQuestions = subs.length > 0 ? subs : [question];
  onProgress({
    phase: "planning", fraction: 0.12, label: `Planned ${subQuestions.length} angle${subQuestions.length === 1 ? "" : "s"}`,
    subQuestions, total: subQuestions.length,
  });

  // 2) GATHER — one billed call per sub-question, run CONCURRENTLY.
  //
  // These were sequential, and that was the whole reason a run took ~4 minutes: each angle is a
  // search + three page reads + one AI call, and we waited for all of it before starting the next
  // angle. The angles are INDEPENDENT — nothing in one feeds another — so the wait was pure waste.
  // Measured 2026-08-14 on the live web app: 4 angles, 12 sources, 3m50s.
  //
  // Concurrency is CAPPED rather than unbounded. Every fetch leaves the customer's own browser, so
  // firing all angles at once would mean ~16 simultaneous requests from one IP at one reader — the
  // fastest way to get that customer throttled. Three at a time keeps it brisk and polite.
  const findings: { sub_question: string; notes: string; sources: RawSource[] }[] = new Array(subQuestions.length);
  let runningSources = 0;
  let completed = 0;

  const gatherOne = async (sq: string, i: number) => {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    // RETRIEVE ON THIS DEVICE (or in this browser), then let the server digest what we found.
    //
    // Why not let the model search: the server asked the model's own provider to search, and that
    // tool is wired for ONE vendor (Anthropic). Scout is BYOK-only, so the model pool is exactly
    // the keys the customer holds — bring any other key and the flag was silently dropped while
    // the prompt still said "use your web-search tool". A model told it has a tool it does not
    // have is how a citation gets invented. Customer-side search is vendor-agnostic and costs
    // Oriora nothing (Pattern 7 — the locked per-surface rule).
    //
    // It also makes a fabricated source unlikely by construction: the URLs we normally hand back
    // are the ones actually opened, never a URL a model typed. NOT impossible, and this comment
    // used to say it was (corrected 2026-09-24) — see the fallback a few lines below: when an
    // angle's device read comes back empty we use the server's own source list for that angle,
    // and the brief's bibliography is the synthesize echo, not this array. Say "unlikely", not
    // "can't" — the first screen and PARITY-MATRIX row 4 are worded to match.
    const hits = await searchWeb(sq);
    const read = await readResults(hits, 3);
    const g = await brainCall<GatherResult>(
      "gather",
      {
        question,
        sub_question: sq,
        // Older server builds ignore this and fall back to the provider-native path.
        retrieved: read.map((r) => ({ title: r.title, url: r.url, snippet: r.snippet, text: r.text })),
      },
      signal,
    );
    const serverSources = Array.isArray(g.sources) ? g.sources.filter((s) => s && typeof s.url === "string") : [];
    const sources = read.length
      ? read.map((r) => ({ title: r.title, url: r.url, snippet: r.snippet }))
      : serverSources;
    findings[i] = { sub_question: sq, notes: String(g.notes ?? ""), sources };

    // Angles now finish out of order, so progress counts COMPLETED angles rather than pretending
    // to walk a list in order — the checklist fills in as each one lands.
    runningSources += sources.length;
    completed += 1;
    onProgress({
      phase: "gathering",
      fraction: 0.15 + (0.6 * completed) / subQuestions.length,
      label: `Gathered ${sources.length} source${sources.length === 1 ? "" : "s"} on “${sq}”`,
      step: completed, total: subQuestions.length, sourcesFound: runningSources, subQuestions,
    });
  };

  onProgress({
    phase: "gathering", fraction: 0.15,
    label: `Researching ${subQuestions.length} angles…`,
    step: 0, total: subQuestions.length, sourcesFound: 0, subQuestions,
  });

  const MAX_CONCURRENT = 3;
  const queue = subQuestions.map((sq, i) => ({ sq, i }));
  await Promise.all(
    Array.from({ length: Math.min(MAX_CONCURRENT, queue.length) }, async () => {
      for (;;) {
        const next = queue.shift();
        if (!next) return;
        await gatherOne(next.sq, next.i);
      }
    }),
  );
  // A thrown angle rejects the whole run, same as before; nothing half-finished reaches synthesize.

  // 3) SYNTHESIZE — one billed call: produce the cited brief from the findings.
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
  onProgress({ phase: "synthesizing", fraction: 0.82, label: "Writing the cited brief…", sourcesFound: runningSources, subQuestions });
  // The photographed sources ride along as image input. Older server builds ignore the `images`
  // field (same forward-compatible pattern as `retrieved` in gather) — the photos then simply do
  // not contribute, rather than breaking the run.
  const syn = await brainCall<SynthesizeResult>(
    "synthesize",
    { question, findings, images: photos.length ? photos : undefined },
    signal,
  );

  // Normalize the synthesized brief defensively (the server is the prompt-holder, but the client
  // never trusts shape blindly — a malformed brief should never crash the UI).
  const sources = (Array.isArray(syn.sources) ? syn.sources : [])
    .filter((s) => s && typeof s.url === "string" && typeof s.title === "string")
    .map((s, idx) => ({ ordinal: typeof s.ordinal === "number" ? s.ordinal : idx + 1, title: s.title, url: s.url, snippet: s.snippet }));
  const validOrdinals = new Set(sources.map((s) => s.ordinal));
  const key_points = (Array.isArray(syn.key_points) ? syn.key_points : [])
    .filter((k) => k && typeof k.text === "string")
    .map((k) => ({
      text: k.text,
      // Hallucinated-citation guard: keep only ordinals that map to a real source we have.
      source_ordinals: Array.isArray(k.source_ordinals) ? k.source_ordinals.filter((o) => validOrdinals.has(o)) : [],
    }));
  const summary = String(syn.summary ?? "").trim();

  // Append the photographed sources to the brief's source list so they PERSIST and render as
  // thumbnails in the bibliography and history, regardless of what the model echoed back. They take
  // ordinals continuing after the web sources, carry `photo_ref` (the on-device JPEG data-URL) and
  // an empty `url` (the source IS the photo, not a link).
  const maxOrdinal = sources.reduce((m, s) => Math.max(m, s.ordinal), 0);
  const photoSourceRows = photos.map((dataUrl, i) => ({
    ordinal: maxOrdinal + i + 1, // continue past the web sources — never collide with a cited ordinal
    title: `Photographed source ${i + 1}`,
    url: "",
    snippet: "",
    photo_ref: dataUrl,
  }));
  const allSources = [...sources, ...photoSourceRows];

  onProgress({ phase: "done", fraction: 1, label: "Done", sourcesFound: allSources.length, subQuestions });
  return { summary, key_points, sources: allSources };
}

// ── Cloud backup (the user owns their data) ─────────────────────────────────────
// The device builds a FAITHFUL backup locally (export_backup); these relay it to the user's OWN
// connected cloud store — their GitHub repo or Google Drive (connected once in the Oriora
// account). Oriora RETAINS nothing. Plain HTTP → works on iOS. Endpoints: the generic
// POST/GET /api/apps/oriora_scout/backup (shared server route).
export interface CloudBackupState { connected: boolean; provider?: string; data?: unknown }

async function authedFetch(endpoint: string, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  return fetch(`${SERVER}/api/apps/${SLUG}/${endpoint}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), authorization: `Bearer ${token}` },
  });
}

/** Save the local backup envelope to the user's connected cloud store. Throws BrainError('no_store')
 *  when nothing is connected, so the UI can point them at the Connections hub. */
export async function cloudBackupSave(envelope: unknown): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("backup", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ data: envelope }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to back up to the cloud.");
  // 413 = over the standard path's size limit (photo-heavy backup). Not retryable — the caller
  // switches to the device-direct GitHub save, or tells the customer how to enable it.
  if (res.status === 413) throw new BrainError("too_large", "This backup is too large for the standard path.");
  if (!res.ok) throw new BrainError("unavailable", "Cloud backup failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Save arbitrary text — a note the user dictates, or the whole conversation — to the user's OWN cloud
 *  store (their GitHub/Drive). Honors an explicit destination when the user names one. Oriora retains
 *  nothing (text relay only). Throws no_store when nothing is connected. Endpoint: the shared
 *  POST /api/apps/oriora_scout/store route (distinct from the whole-collection `backup` route). */
export async function cloudStoreSave(
  content: string,
  opts?: { name?: string; kind?: string; destination?: "github" | "gdrive" },
): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("store", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ content, name: opts?.name, kind: opts?.kind, provider: opts?.destination }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to save to the cloud.");
  if (!res.ok) throw new BrainError("unavailable", "Saving to your cloud failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Read the cloud backup for restore. `connected:false` when no store is connected.
 *  Pass `provider` to read a NAMED source (the RESTORE STORY's explicit restore) — the server
 *  409s rather than silently reading a different cloud than the one the customer asked for. */
export async function cloudBackupLoad(provider?: "github" | "gdrive"): Promise<CloudBackupState> {
  let res: Response;
  try { res = await authedFetch(provider ? `backup?provider=${provider}` : "backup"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", `${provider === "gdrive" ? "Google Drive" : "GitHub"} isn't connected in your Oriora account.`);
  if (!res.ok) throw new BrainError("unavailable", "Couldn't read your cloud backup.");
  return (await res.json()) as CloudBackupState;
}

/** The RESTORE STORY status line — which clouds are connected, which one backups WRITE to, and the
 *  dated backup each holds. Ground truth from the relay (CUSTOMER-CLOUD-STORAGE.md §THE RESTORE STORY). */
export interface CloudBackupSlot { backup_exists: boolean; updated_at: string | null; bytes: number | null }
export interface CloudBackupStatusInfo {
  connected: ("github" | "gdrive")[];
  writes_to: "github" | "gdrive" | null;
  slots: Partial<Record<"github" | "gdrive", CloudBackupSlot>>;
}
export async function cloudBackupStatus(): Promise<CloudBackupStatusInfo> {
  let res: Response;
  try { res = await authedFetch("backup/status"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (!res.ok) throw new BrainError("unavailable", "Couldn't check your backup status.");
  return (await res.json()) as CloudBackupStatusInfo;
}

// Login opens the ecosystem web login INSIDE the app (its own WebKit window), not the OS browser —
// the in-app https handback is robust on iOS where the custom-scheme deep-link is fragile.
// Session returns via auth:deep-link (App.tsx + ipc.rs). The web callback must allowlist
// `oriora-scout` as a return target (server-side, operator task).
export const openWebLogin = () => invoke<void>("open_login_window");

// ─────────────────────────────────────────────────────────────────────────────
// SERVER PROMPT CONTRACT (operator must mirror at oriora-server for the three Scout endpoints).
// Kept here as the source-of-truth contract. Scout is BYOK-only (agentic ⇒ default_byok=true); the
// server gates Managed AI with a 403. Each endpoint is ONE grounded LLM call.
// ─────────────────────────────────────────────────────────────────────────────
export const PLAN_SYSTEM_PROMPT = `You are the PLANNER of Oriora Scout, a deep-research agent. Given a user's research question, break it into 2–4 focused, non-overlapping sub-questions that together fully answer it. Prefer concrete, searchable angles (definitions, mechanisms, comparisons, evidence, caveats). Do not answer; only plan. Return JSON: { "sub_questions": string[] }.`;

export const GATHER_SYSTEM_PROMPT = `You are the GATHERER of Oriora Scout. Research ONE sub-question using web search. Return the most relevant, credible sources you found and a short factual digest ("notes") of what they say — grounded ONLY in those sources, no outside claims, no opinions. Each source must be a real result with a working URL and a title. Return JSON: { "sources": [{ "title": string, "url": string, "snippet": string }], "notes": string }.`;

// PHOTOGRAPHED SOURCES (image input): when the synthesize request carries an `images` array of
// data-URLs, those are pages/reports/whiteboards/slides the user photographed and attached as
// sources. Attach them as image input to the synthesize LLM call and treat them as first-class
// sources — ground claims in what they show, alongside the web findings. (The client appends them
// to the returned source list itself, so the server need only READ them; it does not have to echo
// them back.) Older builds without image support ignore the field and simply do not use the photos.
export const SYNTHESIZE_SYSTEM_PROMPT = `You are the SYNTHESIZER of Oriora Scout. You are given the user's question and the per-sub-question FINDINGS (notes + sources) gathered earlier. Write a structured, CITED brief:
1. A clear 2–4 sentence "summary" answering the question directly.
2. "key_points": 3–7 concrete points. EVERY point must cite the source(s) it came from via "source_ordinals" — 1-based indices into your "sources" list. Never state a claim that no source supports.
3. "sources": the de-duplicated list of sources you actually used, each with a 1-based "ordinal", "title", "url", "snippet".
If photographed source images are attached to this request, READ them and treat them as additional first-class sources you may ground claims in (they were captured by the user — a page, printed report, whiteboard or slide). RULES: ground every claim in a provided source; never invent a source, url, figure or date; be factual and neutral (report what sources say, do not advise). Return JSON: { "summary": string, "key_points": [{ "text": string, "source_ordinals": number[] }], "sources": [{ "ordinal": number, "title": string, "url": string, "snippet": string }] }.`;
